<template>
  <tr
    v-if="container && container.order"
    class="tw-cursor-zoom-in odd:tw-bg-slate-50/40 hover:tw-bg-slate-900/5"
    @click="toggleTripStatus(container.id)"
  >
    <td class="tw-px-4 tw-py-3 tw-font-medium tw-text-slate-900 tw-whitespace-nowrap">
      <CopyButton
        :value="container.containerId"
        mode="wrap-content"
        title="Copy container number"
        aria-label="Copy container number to clipboard"
      >
        {{ container.containerId }}
      </CopyButton>
    </td>
    <td class="tw-px-4 tw-py-3 tw-whitespace-nowrap">
      <span
        v-if="container.order.orderType && container.order.orderType.includes('Import')"
        class="tw-inline-flex tw-items-center tw-rounded-md tw-bg-indigo-500/15 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-indigo-800"
      >
        Import
      </span>
      <span
        v-else-if="container.order.orderType && container.order.orderType.includes('Rail')"
        class="tw-inline-flex tw-items-center tw-rounded-md tw-bg-indigo-500/15 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-indigo-800"
      >
        Rail
      </span>
      <span
        v-else
        class="tw-inline-flex tw-items-center tw-rounded-md tw-bg-amber-500/15 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-amber-800"
      >
        Export
      </span>
    </td>
    <td class="tw-px-4 tw-py-3 tw-whitespace-nowrap">{{ container.size }}{{ container.type }}</td>
    <td class="tw-px-4 tw-py-3 tw-whitespace-nowrap">
      <span
        v-if="container.order?.isExport"
        class="tw-inline-flex tw-items-center tw-px-2.5 tw-py-1 tw-font-medium tw-text-slate-700"
      >
        <CopyButton
          :value="container.order?.bookingNumber"
          mode="wrap-content"
          title="Copy booking number"
          aria-label="Copy booking number to clipboard"
        >
          {{ container.order?.bookingNumber }}
        </CopyButton>
      </span>
      <span
        v-else-if="container.order?.billOfLading"
        class="tw-inline-flex tw-items-center tw-px-2.5 tw-py-1 tw-font-medium tw-text-slate-700"
      >
        <CopyButton
          :value="container.order?.billOfLading?.billOfLadingId"
          mode="wrap-content"
          title="Copy bill of lading number"
          aria-label="Copy bill of lading number to clipboard"
        >
          {{ container.order?.billOfLading?.billOfLadingId }}
        </CopyButton>
      </span>
    </td>
    <td class="tw-px-4 tw-py-3 tw-whitespace-nowrap">
      {{ nextTripForContainers && nextTripForContainers.origin ? nextTripForContainers.origin.name : '' }}
    </td>
    <td class="tw-px-4 tw-py-3 tw-whitespace-nowrap">
      {{ nextTripForContainers && nextTripForContainers.destination ? nextTripForContainers.destination.name : '' }}
    </td>
    <td class="tw-px-4 tw-py-3 tw-whitespace-nowrap">
      <TripTypeField
        v-if="nextTripForContainers"
        :showLabel="false"
        :trip="nextTripForContainers"
        :isEditing="false"
        :darkGreen="nextTripForContainers.status == 'Complete'"
        :gray="
          nextTripForContainers.status != 'Complete' &&
          nextTripForContainers.status != 'In Progress'
        "
        :green="nextTripForContainers.status == 'In Progress'"
      ></TripTypeField>
    </td>
    <td class="tw-px-4 tw-py-3 tw-whitespace-nowrap">
      {{ nextTripForContainers && nextTripForContainers.driver ? nextTripForContainers.driver.fullName : '' }}
    </td>
    <td class="tw-px-4 tw-py-3 tw-whitespace-nowrap">
      <CopyButton
        v-if="nextTripForContainers"
        :value="nextTripForContainers.chassis"
        mode="wrap-content"
        title="Copy chassis number"
        aria-label="Copy chassis number to clipboard"
      >
        {{ nextTripForContainers.chassis }}
      </CopyButton>
    </td>
    <td class="tw-px-4 tw-py-3 tw-whitespace-nowrap">{{ container.order?.billTo?.name || '' }}</td>
    <td class="tw-px-4 tw-py-3 tw-whitespace-nowrap">{{ container.order?.receiver?.name || '' }}</td>
  </tr>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

import TripTypeField from '../Orders/UI/TripTypeField.vue';
import CopyButton from '../core/CopyButton.vue';

type DispatchTrip = {
  legId: number | string;
  status?: string | null;
  origin?: { name?: string | null } | null;
  destination?: { name?: string | null } | null;
  driver?: { fullName?: string | null } | null;
  chassis?: string | null;
};

type DispatchContainer = {
  id: number;
  containerId?: string | null;
  size?: string | null;
  type?: string | null;
  order?: {
    id: string;
    orderType?: string | null;
    isExport?: boolean | null;
    bookingNumber?: string | null;
    billOfLading?: { billOfLadingId?: string | null } | null;
    billTo?: { name?: string | null } | null;
    receiver?: { name?: string | null } | null;
  } | null;
  trips: DispatchTrip[];
};

export default defineComponent({
  props: {
    container: {
      type: Object as PropType<DispatchContainer>,
      required: true,
    },
  },
  methods: {
    toggleTripStatus(containerId: number) {
      (this as any).$emit('toggleTripStatus', containerId);
    },
  },
  components: {
    TripTypeField,
    CopyButton,
  },
  computed: {
    nextTripForContainers(): DispatchTrip | undefined {
      const trips = ((this as any).container as DispatchContainer).trips
        .slice()
        .sort((a: DispatchTrip, b: DispatchTrip) => {
          return Number(a.legId) - Number(b.legId);
        });

      let filtered = trips.filter(
        (x) => x.status === 'In Progress' || !x.status,
      );
      if (filtered.length > 0) {
        return filtered[0];
      } else {
        filtered = trips.filter((x) => x.status === 'Not Started');
        if (filtered.length > 0) {
          return filtered[0];
        }
      }

      return trips[0];
    },
    // then find not started
  },
});
</script>